import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { keyBy } from 'lodash-es';
import { HttpErrorResponse } from '@angular/common/http';
import { mergeMap } from 'rxjs/operators';
import { Client } from '../../../core/models/client.model';
import { ClientConfigurationOptionDefinition } from '../../../core/models/client-configuration-option-definition.model';
import { ClientsService } from '../../../core/services/clients.service';
import {
  ClientConfigurationOptionsService,
  PersistableClientConfigurationOption,
} from '../../../core/services/client-configuration-options.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { ClientConfigurationOption } from '../../../core/models/client-configuration-option.model';
import {
  ConfigsMap,
  DefinitionsMap,
  determineDeactivated,
  determineEditorType,
  EditableConfiguration,
  EditorType,
  generateConfigIdentifier,
  initializeSuboptionChoices,
  initializeMultiselectChoices,
  isDefinitionSelectable,
  isPersisted,
  normalizeConfigValueForUpdate,
  refreshConfigValidity,
  refreshDependencyWarnings,
  sortConfigs,
  SuboptionChoice,
} from '../../lib/client-configuration';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ClientConfigurationTitleComponent } from '../../components/client-configuration-title/client-configuration-title.component';
import { ClientConfigurationDatedValueComponent } from '../../components/client-configuration-dated-value/client-configuration-dated-value.component';
import { ClientConfigurationMultiselectComponent } from '../../components/client-configuration-multiselect/client-configuration-multiselect.component';
import { ClientConfigurationSuboptionsComponent } from '../../components/client-configuration-suboptions/client-configuration-suboptions.component';
import { ClientConfigurationBasicValueComponent } from '../../components/client-configuration-basic-value/client-configuration-basic-value.component';
import { ClientConfigurationDisableableComponent } from 'app/capture-admin/components/client-configuration-disableable/client-configuration-disableable.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-capture-admin-client-edit-configuration',
    templateUrl: './capture-admin-client-edit-configuration.component.html',
    styleUrls: ['./capture-admin-client-edit-configuration.component.scss'],
    imports: [RouterLink,
              FormsModule,
              NgClass,
              NgSelectComponent,
              ClientConfigurationTitleComponent,
              ClientConfigurationDatedValueComponent,
              ClientConfigurationMultiselectComponent,
              ClientConfigurationSuboptionsComponent,
              ClientConfigurationBasicValueComponent,
              ClientConfigurationDisableableComponent,
              MatProgressSpinner]
})
export class CaptureAdminClientEditConfigurationComponent implements OnInit {
  private clientsService = inject(ClientsService);
  private clientConfigurationOptionsService = inject(ClientConfigurationOptionsService);
  private navigationService = inject(NavigationService);

  @ViewChild('configForm', { static: false }) form: any;

  client: Client;
  loading = false;
  saving = false;
  editorTypes = EditorType;

  trackByConfig = (index: number, config: EditableConfiguration) => config.identifier;

  allDefinitionsByName: DefinitionsMap = {};
  selectableDefinitions: ClientConfigurationOptionDefinition[] = null;
  selectedDefinition: ClientConfigurationOptionDefinition = null;

  actualConfigsByIdentifier: ConfigsMap = {};
  displayedConfigs: EditableConfiguration[] = [];

  ngOnInit() {
    const clientId = this.navigationService.currentId;
    this.loadConfigs(clientId);
  }

  onAddNewConfig(ngSelectControl) {
    this.addNewConfig(ngSelectControl);
  }

  onBasicChange(config: EditableConfiguration, val: any) {
    config.configOption.value = val;
    refreshConfigValidity(this.form, config);
    this.updateDependencyWarnings();
  }

  onBasicDeleteClick(config: EditableConfiguration, $event) {
    $event.preventDefault();
    this.deleteConfig(config);
  }

  onMultiSelectChange(config: EditableConfiguration, selected: string[]) {
    config.configOption.value = selected;
    refreshConfigValidity(this.form, config);
    this.updateDependencyWarnings();
  }

  onSuboptionsChange(config: EditableConfiguration, suboptionChoices: SuboptionChoice[]) {
    this.updateSuboptions(config, suboptionChoices);
    this.updateDependencyWarnings();
  }

  onDatedChange(config: EditableConfiguration, configOption: ClientConfigurationOption) {
    config.configOption = configOption;
    refreshConfigValidity(this.form, config);
    this.updateDependencyWarnings();
  }

  onDatedDelete(config: EditableConfiguration) {
    this.deleteConfig(config);
  }

  onEnableChange(config: EditableConfiguration, val: any) {
    config.configOption.enabled = val;
    this.updateDependencyWarnings();
  }

  onFormSubmit(form) {
    const theForm = form.form;
    this.saveConfigs(theForm);
  }

  onCancelClick() {
    this.navigateToClient();
  }

  private loadConfigs(clientId): void {
    this.loading = true;

    this.clientsService
      .get(clientId)
      .pipe(
        mergeMap((client: Client) => {
          this.client = client;
          return this.clientConfigurationOptionsService.getAllDefinitions();
        }),
        mergeMap(({ availableClientConfigurationOptions }) => {
          this.allDefinitionsByName = keyBy(availableClientConfigurationOptions, 'option');
          return this.clientConfigurationOptionsService.getList(this.client.id);
        })
      )
      .subscribe(({ clientConfigurationOptions }) => {
        this.initializeConfigs(clientConfigurationOptions);
        this.loading = false;
      });
  }

  private initializeConfigs(clientConfigs: ClientConfigurationOption[]) {
    const initialConfigs = clientConfigs.map(config => {
      const definition = this.allDefinitionsByName[config.option];

      const eco = new EditableConfiguration(
        generateConfigIdentifier(definition.option, config.id),
        config,
        definition,
        determineEditorType(definition),
        determineDeactivated(definition, config)
      );

      initializeSuboptionChoices(definition, eco);
      initializeMultiselectChoices(definition, eco, this.client);

      return eco;
    });

    this.actualConfigsByIdentifier = keyBy(initialConfigs, 'identifier');
    this.refreshLists();
  }

  private refreshLists() {
    this.refreshSelectableDefinitions();
    this.refreshDisplayedConfigs();
    this.updateDependencyWarnings();
  }

  private updateDependencyWarnings() {
    refreshDependencyWarnings(this.actualConfigsByIdentifier, this.allDefinitionsByName);
  }

  private refreshSelectableDefinitions() {
    this.selectableDefinitions = Object.values(this.allDefinitionsByName)
      .filter(kod => isDefinitionSelectable(kod, this.actualConfigsByIdentifier))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  private refreshDisplayedConfigs() {
    this.displayedConfigs = sortConfigs(Object.values(this.actualConfigsByIdentifier).filter(opt => !opt.deleted));
  }

  private updateSuboptions(config: EditableConfiguration, suboptionChoices: SuboptionChoice[]) {
    config.suboptionChoices = suboptionChoices;
    refreshConfigValidity(this.form, config);
  }

  private addNewConfig(ngSelectControl) {
    if (this.selectedDefinition) {
      const identifier = generateConfigIdentifier(this.selectedDefinition.option, null);
      const matchingConfig = this.actualConfigsByIdentifier[identifier];

      if (!matchingConfig) {
        const newConfigOption = new ClientConfigurationOption();
        newConfigOption.option = this.selectedDefinition.option;
        newConfigOption.value = this.selectedDefinition.defaultValue;

        if (this.selectedDefinition.dated) {
          newConfigOption.value = true;
        }

        const newEditableOption = new EditableConfiguration(
          identifier,
          newConfigOption,
          this.selectedDefinition,
          determineEditorType(this.selectedDefinition)
        );
        newEditableOption.stagedAt = new Date();

        initializeSuboptionChoices(this.selectedDefinition, newEditableOption);
        initializeMultiselectChoices(this.selectedDefinition, newEditableOption, this.client);

        this.actualConfigsByIdentifier[newEditableOption.identifier] = newEditableOption;
      } else {
        matchingConfig.deleted = false;
        initializeSuboptionChoices(this.selectedDefinition, matchingConfig);
        initializeMultiselectChoices(this.selectedDefinition, matchingConfig, this.client);
      }
    }

    ngSelectControl.handleClearClick();
    this.refreshLists();
  }

  private deleteConfig(config: EditableConfiguration) {
    if (!isPersisted(config.configOption)) {
      delete this.actualConfigsByIdentifier[config.identifier];
    } else {
      config.deleted = true;
    }

    this.refreshLists();
  }

  private saveConfigs(theForm) {
    if (theForm.valid) {
      this.saving = true;
      const payload = this.normalizeConfigsForSave();
      this.clientConfigurationOptionsService.updateAll(this.client.id, payload).subscribe(
        ({ clientConfigurationOptions }) => this.handleSaveSuccess(clientConfigurationOptions),
        response => this.handleSaveFailure(response)
      );
    }
  }

  private normalizeConfigsForSave() {
    return Object.values(this.actualConfigsByIdentifier)
      .filter(eco => !eco.deactivated && eco.definition.editable)
      .map(eco => {
        const normalizedOption = new PersistableClientConfigurationOption();

        if (isPersisted(eco.configOption)) {
          normalizedOption.id = eco.configOption.id;
        }

        if (eco.editorType === EditorType.dated) {
          normalizedOption.startAt = eco.configOption.startAt;
          normalizedOption.endAt = eco.configOption.endAt;
        }

        normalizedOption.option = eco.definition.option;
        normalizedOption.deleted = eco.deleted;
        normalizedOption.value = normalizeConfigValueForUpdate(eco);
        normalizedOption.disableable = eco.configOption.disableable;
        normalizedOption.enabled = eco.configOption.enabled;

        return normalizedOption;
      });
  }

  private handleSaveSuccess(clientConfigs: ClientConfigurationOption[]) {
    this.initializeConfigs(clientConfigs);
    this.navigateToClient();
  }

  private handleSaveFailure(response: HttpErrorResponse) {
    this.saving = false;

    if (response.status === 400) {
      const { error: data } = response;

      data.errors.forEach(error => {
        const identifier = generateConfigIdentifier(error.option, error.id);
        const config = this.actualConfigsByIdentifier[identifier];
        if (config) {
          refreshConfigValidity(this.form, config, error);
        }
      });
    } else {
      console.error(response);
    }
  }

  private navigateToClient() {
    this.navigationService.navigateTo(`/capture-admin/client-management/clients/${this.client.id}`);
  }
}
